import { forwardRef, useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';

import { Button, Form, Spinner, Text, VStack, theme } from '@squire/ui';

import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import { LegalAgreementFootnote } from '@/components/_shared/LegalAgreementFootnote';
import { ScreenHeader } from '@/components/_shared/ScreenHeader';
import { ShopDetails } from '@/components/_shared/ShopDetails';
import { UserInformationForm } from '@/components/_shared/UserInformationForm';
import { createUserInformationSchema } from '@/components/_shared/UserInformationForm/schema';
import { ConfirmBookingSignInPrompt } from '@/components/booking/ConfirmBookingSignInPrompt';
import { ConfirmBookingSkeleton } from '@/components/booking/ConfirmBookingSkeleton';
import { StandbyWaitlistSummary } from '@/components/booking/StandbyWaitlistSummary';
import { storage, storageKeys } from '@/constants/storage';
import { useLegalBottomSheet } from '@/context/legal';
import { useConfirmOrderForm } from '@/hooks/booking/useConfirmOrderForm';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import composeRefs from '@/utils/composeRefs';

import { styles } from './styles';
import type { StandbyWaitlistJoinSheetProps } from './types';

const StandbyWaitlistJoinSheet = forwardRef<
  BottomSheetRef,
  StandbyWaitlistJoinSheetProps
>(
  (
    {
      shop,
      cartItem,
      day,
      timePreferences,
      currency,
      isJoining,
      onJoin,
      onDismiss,
      testID = 'standby-waitlist-join-sheet',
    },
    ref,
  ) => {
    const { t } = useScreenTranslations('StandbyWaitlistJoinSheet');
    const { t: tCheckout } = useScreenTranslations('ConfirmBookingBottomSheet');
    const { t: tValidation } = useScreenTranslations(
      'ConfirmBookingBottomSheet.userInformation.validation',
    );
    const { openTerms, openPrivacy } = useLegalBottomSheet();
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [profileIncompleteAtOpen, setProfileIncompleteAtOpen] = useState<
      boolean | null
    >(null);

    const {
      form,
      isFormValid,
      handleSubmit: handleJoin,
      handleDismiss,
      closeBottomSheet,
      bottomSheetRef,
      customer,
      isCustomerSettling,
    } = useConfirmOrderForm({
      requirePaymentMethod: false,
      onSubmit: ({ firstName, lastName, phone, email }) =>
        onJoin({ firstName, lastName, phone, email }),
      onDismiss: () => {
        storage.remove(storageKeys.authReturnPath);
        setIsSheetOpen(false);
        setProfileIncompleteAtOpen(null);
        onDismiss?.();
      },
    });

    const isProfileIncomplete = useCallback(
      (profile: NonNullable<typeof customer>) =>
        !createUserInformationSchema(tValidation).safeParse({
          firstName: profile.firstName ?? '',
          lastName: profile.lastName ?? '',
          phone: profile.phone ?? '',
          email: profile.email ?? '',
        }).success,
      [tValidation],
    );

    // Latched per open so a customer refetch can't make the form vanish
    // while the client is filling in their missing details.
    useEffect(() => {
      if (!isSheetOpen || !customer || profileIncompleteAtOpen !== null) return;

      setProfileIncompleteAtOpen(isProfileIncomplete(customer));
    }, [isSheetOpen, customer, profileIncompleteAtOpen, isProfileIncomplete]);

    const showForm =
      !customer || (profileIncompleteAtOpen ?? isProfileIncomplete(customer));

    return (
      <BottomSheet
        hideHandle
        testID={testID}
        dismissible={!isJoining}
        onOpen={() => setIsSheetOpen(true)}
        onDismiss={handleDismiss}
        ref={composeRefs(ref, bottomSheetRef)}
        style={styles.bottomSheet}
        containerStyle={styles.background}
        maxDynamicContentRatio={0.95}
        scrollProps={{
          showsVerticalScrollIndicator: false,
        }}
      >
        <View>
          <ScreenHeader
            title={t('title')}
            titleAccessibilityRole="header"
            onClose={() => {
              if (!isJoining) closeBottomSheet();
            }}
          />

          {!isCustomerSettling && !customer && <ConfirmBookingSignInPrompt />}
        </View>

        {isCustomerSettling ? (
          <ConfirmBookingSkeleton />
        ) : (
          <Form {...form}>
            <ShopDetails shop={shop} withImageFallback />

            {showForm && <UserInformationForm />}

            {cartItem && (
              <StandbyWaitlistSummary
                cartItem={cartItem}
                day={day}
                timePreferences={timePreferences}
                currency={currency}
              />
            )}

            <VStack space={theme.spacing[3]}>
              <Button
                testID="standby-waitlist-join-button"
                onPress={handleJoin}
                disabled={!isFormValid || isJoining}
                variant="primary"
                size="medium"
                accessibilityRole="button"
                accessibilityLabel={t('cta')}
                accessibilityHint={t('accessibility.ctaHint')}
                aria-disabled={!isFormValid || isJoining || undefined}
              >
                {isJoining ? (
                  <View
                    style={styles.loadingContent}
                    testID="standby-waitlist-join-spinner"
                  >
                    <Spinner color="textPrimaryInverse" size="small" />
                  </View>
                ) : (
                  <Text color="textPrimaryInverse" variant="lStrong">
                    {t('cta')}
                  </Text>
                )}
              </Button>

              <LegalAgreementFootnote
                introText={t('legalIntro')}
                termsLabel={tCheckout('termsOfService')}
                conjunctionText={tCheckout('and')}
                privacyLabel={tCheckout('privacyPolicy')}
                onTermsPress={openTerms}
                onPrivacyPress={openPrivacy}
              />
            </VStack>
          </Form>
        )}
      </BottomSheet>
    );
  },
);

StandbyWaitlistJoinSheet.displayName = 'StandbyWaitlistJoinSheet';
export { StandbyWaitlistJoinSheet };
